import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Bell, Building2, Camera, Check, Clock, Globe2, KeyRound, Mail, MapPin, Palette, Pencil, Phone, ShieldCheck, Sparkles, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import AppearanceSettings from '../components/AppearanceSettings';
import AuthImage from '../components/AuthImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CAPABILITIES, isAdminOrPlatform } from '../lib/access';
import { guidesFor } from '../lib/guides';

const NOTIFICATION_CHOICES = [
  ['query_resolved', 'Query resolved'],
  ['flagged_updates', 'Query and escalation updates'],
  ['new_knowledge', 'New team knowledge'],
  ['floor_mentions', 'Floor Space mentions'],
  ['email_enabled', 'Email delivery'],
];
const ABILITY_LABELS = { ...Object.fromEntries(CAPABILITIES.map(({ key, label }) => [key, label])), can_delete: 'Delete knowledge' };
const SCOPE_LABELS = { organisation: 'whole organisation', teams: 'specific teams' };
const emptyPassword = { current: '', next: '', confirm: '', saving: false };
const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || 'U';

const Panel = ({ title, icon: Icon, action, children, testId, className = '' }) => (
  <section className={`qflow-home-card p-5 ${className}`} data-testid={testId} id={testId}>
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="flex items-center gap-2 text-base font-bold">{Icon && <Icon size={17} className="qflow-accent-text" aria-hidden="true" />}{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Stat = ({ value, label }) => (
  <div className="qflow-home-card p-4"><p className="qflow-home-number">{value}</p><p className="mt-1 text-sm">{label}</p></div>
);

const Fact = ({ icon: Icon, children }) => (
  <p className="flex items-center gap-2 text-sm"><Icon size={14} className="shrink-0 text-gray-400" aria-hidden="true" />{children}</p>
);

// What opens when you click your name at the bottom of the sidebar, for every level.
const Profile = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const fileInput = useRef(null);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [password, setPassword] = useState(emptyPassword);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: body } = await axiosInstance.get('/users/me/profile');
      setData(body);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      toast.error('Could not load your profile');
    }
  }, []);

  useEffect(() => {
    axiosInstance.get('/auth/session').then(({ data: body }) => setSession(body)).catch(() => setSession(null));
    load();
    axiosInstance.get('/notification-settings').then(({ data: body }) => setPrefs(body)).catch(() => setPrefs(null));
  }, [load]);

  useEffect(() => {
    if (data && location.hash.startsWith('#account-')) document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [data, location.hash]);

  const updatePrefs = async (next) => {
    const previous = prefs;
    setPrefs(next);
    try {
      await axiosInstance.put('/notification-settings', next);
      toast.success('Notification choices saved');
    } catch (_) {
      setPrefs(previous);
      toast.error('Could not save notification choices');
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (password.next !== password.confirm) { toast.error('New passwords do not match'); return; }
    setPassword((current) => ({ ...current, saving: true }));
    try {
      await axiosInstance.post('/auth/change-password', { current_password: password.current, new_password: password.next });
      toast.success('Password changed. Sign in again with your new password.');
      localStorage.removeItem('token');
      window.location.assign('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not change password');
      setPassword((current) => ({ ...current, saving: false }));
    }
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Choose a photo under 5 MB'); return; }
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const { data: body } = await axiosInstance.post(`/users/${user.id}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setData((current) => ({ ...current, profile: { ...current.profile, photo_url: body.photo_url } }));
      if (setUser) setUser((current) => (current ? { ...current, photo_url: body.photo_url } : current));
      toast.success('Photo updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not upload the photo');
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    try {
      await axiosInstance.patch(`/users/${user.id}/profile`, editing);
      setData((current) => ({ ...current, profile: { ...current.profile, ...editing } }));
      setEditing(null);
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save your profile');
    }
  };

  if (loadError && !data) return <div className="p-8"><p role="alert">Could not load your profile.</p><Button className="mt-3" onClick={load}>Try again</Button></div>;
  if (!data) return <div className="p-8 text-sm text-gray-500">Loading your profile…</div>;
  const { profile, work, leadership, abilities, numbers, organisation, platform } = data;
  const guides = guidesFor(user);
  const guidesDone = guides.filter((guide) => (data.learning?.guides_done || []).includes(guide.id)).length;
  const subtitle = [work.titles[0], organisation?.name || work.teams[0], profile.location].filter(Boolean).join(' · ');
  const leaderPills = ['floor', 'source'].filter((kind) => leadership[kind] !== 'off').map((kind) => `${kind === 'floor' ? 'Floor' : 'Source'} Leader · ${SCOPE_LABELS[leadership[kind]]}`);
  const canOpenWebMode = !isAdminOrPlatform(user) && user?.permissions?.can_manage_web_mode === true;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="profile-page">
      <div className="mx-auto max-w-5xl space-y-5 px-4 py-8 sm:px-6 lg:px-8">
        <section className="qflow-home-hero flex flex-wrap items-center gap-5" data-testid="profile-header">
          <div className="relative">
            <div className="grid h-24 w-24 place-items-center overflow-hidden rounded-full border-2 text-2xl font-extrabold" style={{ borderColor: 'var(--qflow-accent)', background: 'var(--qflow-accent-soft)', color: 'var(--qflow-accent-strong)' }}>
              <AuthImage src={profile.photo_url} className="h-full w-full object-cover" fallback={initials(profile.name)} />
            </div>
            <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading} className="qflow-accent-bg absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-full" aria-label="Change photo" data-testid="profile-change-photo"><Camera size={15} /></button>
            <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadPhoto} />
          </div>
          <div className="min-w-[14rem] flex-1">
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">{profile.name}</h1>
            {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="qflow-home-pill qflow-home-pill-good" data-testid="profile-access-label">{profile.access_label}</span>
              {leaderPills.map((label) => <span key={label} className="qflow-home-pill qflow-home-pill-warn">{label}</span>)}
            </div>
          </div>
          <button type="button" onClick={() => setEditing({ phone: profile.phone || '', location: profile.location || '', fun_fact: profile.fun_fact || '', hobby: profile.hobby || '' })} className="qflow-home-chip flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold" data-testid="profile-edit"><Pencil size={14} aria-hidden="true" />Edit profile</button>
        </section>

        {organisation && <Panel title={`Your organisation · ${organisation.name}`} icon={Building2} testId="profile-organisation" action={<button type="button" onClick={() => navigate('/admin')} className="qflow-accent-text flex items-center gap-1 text-sm font-semibold hover:underline">Open Admin<ArrowRight size={14} aria-hidden="true" /></button>}>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><p className="qflow-home-number">{organisation.people}</p><p className="text-sm text-gray-500">people</p></div>
            <div><p className="qflow-home-number" style={organisation.to_approve ? { color: '#e0a052' } : undefined}>{organisation.to_approve}</p><p className="text-sm text-gray-500">to approve</p></div>
            <div><p className="qflow-home-number">{organisation.verified_domains}</p><p className="text-sm text-gray-500">verified domains</p></div>
          </div>
        </Panel>}

        {platform && <Panel title="Qflow at a glance" icon={ShieldCheck} testId="profile-platform" action={<button type="button" onClick={() => navigate('/platform-operations')} className="qflow-accent-text flex items-center gap-1 text-sm font-semibold hover:underline">Open Platform Operations<ArrowRight size={14} aria-hidden="true" /></button>}>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><p className="qflow-home-number">{platform.organizations}</p><p className="text-sm text-gray-500">organisations</p></div>
            <div><p className="qflow-home-number" style={platform.awaiting_approval ? { color: '#e0a052' } : undefined}>{platform.awaiting_approval}</p><p className="text-sm text-gray-500">awaiting approval</p></div>
            <div><p className="qflow-home-number">{platform.open_support_cases}</p><p className="text-sm text-gray-500">open support cases</p></div>
          </div>
        </Panel>}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="profile-numbers">
          <Stat value={numbers.questions_asked} label="questions asked" />
          <Stat value={numbers.resolved_rate === null ? '–' : `${numbers.resolved_rate}%`} label="resolved" />
          <Stat value={numbers.floor_answers} label="Floor answers given" />
          <Stat value={`${guidesDone}/${guides.length}`} label="Learn Qflow guides" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="About" icon={UserRound} testId="profile-about">
            <div className="space-y-2">
              <Fact icon={Mail}>{profile.email}</Fact>
              {profile.phone && <Fact icon={Phone}>{profile.phone}</Fact>}
              {profile.location && <Fact icon={MapPin}>{profile.location}</Fact>}
            </div>
            {profile.fun_fact && <div className="mt-3"><p className="text-xs text-gray-500">Something I want people to know</p><p className="text-sm">{profile.fun_fact}</p></div>}
            {profile.hobby && <div className="mt-2"><p className="text-xs text-gray-500">Outside work</p><p className="text-sm">{profile.hobby}</p></div>}
            {!profile.phone && !profile.location && !profile.fun_fact && <p className="mt-3 text-sm text-gray-500">Add a phone number, where you work from and something colleagues should know. It also shows on the Org Chart.</p>}
          </Panel>
          <Panel title="Work in Qflow" icon={Sparkles} testId="profile-work">
            <div className="space-y-1.5 text-sm">
              <p>{work.titles.length ? work.titles.join(', ') : 'No Org Chart role yet'}</p>
              {work.teams.length > 0 && <p className="text-gray-500">{work.teams.join(', ')}</p>}
              {work.reports_to && <p>Reports to {work.reports_to.name ? `${work.reports_to.name}, ` : ''}{work.reports_to.title}</p>}
              {work.direct_reports > 0 && <p>{work.direct_reports} direct report{work.direct_reports === 1 ? '' : 's'}</p>}
            </div>
            <p className="mt-3 text-xs text-gray-500">What you can do</p>
            <ul className="mt-1 space-y-1 text-sm" data-testid="profile-abilities">
              <li className="flex items-center gap-2"><Check size={14} className="qflow-accent-text" aria-hidden="true" />Ask Qflow and use your teams’ knowledge</li>
              {abilities.map((key) => <li key={key} className="flex items-center gap-2"><Check size={14} className="qflow-accent-text" aria-hidden="true" />{ABILITY_LABELS[key]}</li>)}
              {leaderPills.map((label) => <li key={label} className="flex items-center gap-2"><Check size={14} className="qflow-accent-text" aria-hidden="true" />{label}</li>)}
            </ul>
            {profile.level === 'member' && <p className="mt-3 text-xs text-gray-500">Your Tenant Admin decides these.</p>}
          </Panel>
        </div>

        {canOpenWebMode && <Panel title="Web Mode settings" icon={Globe2}>
          <p className="text-sm text-gray-500">Your Tenant Admin has let you manage Web Mode for the organisation.</p>
          <Button variant="outline" className="mt-3" onClick={() => navigate('/admin?section=web-mode')}>Open Web Mode settings</Button>
        </Panel>}

        <h2 className="pt-2 text-lg font-bold">Account</h2>
        <Panel title="Session limits" icon={Clock} testId="account-session">
          <p className="text-sm text-gray-500">{session ? `Your session: ${session.idle_minutes} minutes idle and ${session.max_session_minutes} minutes total. ` : 'Session limits are checked automatically. '}A warning appears two minutes before sign-out. Stay signed in renews the idle timer, but cannot extend the total limit. Your Tenant Admin can choose an idle limit from 3 minutes to 2 hours and a shorter total session limit.</p>
        </Panel>
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Notifications" icon={Bell} testId="account-notifications">
            {prefs ? <div className="space-y-3">{NOTIFICATION_CHOICES.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3"><Label className="text-sm font-normal">{label}</Label><Switch checked={Boolean(prefs[key])} onCheckedChange={(checked) => updatePrefs({ ...prefs, [key]: checked })} /></div>
            ))}</div> : <p className="text-sm text-gray-500">Notification choices are not available right now.</p>}
          </Panel>
          <Panel title="Password and sign-in" icon={KeyRound} testId="account-password">
            <form onSubmit={changePassword} className="grid gap-3">
              <div><Label className="mb-1 block text-sm">Current password</Label><Input type="password" autoComplete="current-password" value={password.current} onChange={(event) => setPassword((current) => ({ ...current, current: event.target.value }))} required /></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="mb-1 block text-sm">New password</Label><Input type="password" autoComplete="new-password" minLength={6} value={password.next} onChange={(event) => setPassword((current) => ({ ...current, next: event.target.value }))} required /></div>
                <div><Label className="mb-1 block text-sm">Confirm</Label><Input type="password" autoComplete="new-password" minLength={6} value={password.confirm} onChange={(event) => setPassword((current) => ({ ...current, confirm: event.target.value }))} required /></div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-xs text-gray-500">At least 6 characters.</span><Button type="submit" disabled={password.saving}>{password.saving ? 'Updating…' : 'Update password'}</Button></div>
            </form>
          </Panel>
        </div>
        <Panel title="Theme" icon={Palette} testId="account-theme"><AppearanceSettings /></Panel>
        <p className="flex items-center gap-1 text-xs text-gray-500"><UserRound size={12} aria-hidden="true" />{profile.level === 'member' ? 'To change your name, username or sign-in email, ask your Tenant Admin.' : 'Change names, usernames and sign-in emails in Teams and Users.'}</p>
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit profile</DialogTitle></DialogHeader>
          {editing && <form onSubmit={saveProfile} className="grid gap-3" data-testid="profile-edit-form">
            <div><Label className="mb-1 block text-sm">Phone</Label><Input value={editing.phone} maxLength={40} onChange={(event) => setEditing({ ...editing, phone: event.target.value })} placeholder="+44 20 7946 0000" /></div>
            <div><Label className="mb-1 block text-sm">Location</Label><Input value={editing.location} maxLength={80} onChange={(event) => setEditing({ ...editing, location: event.target.value })} placeholder="London" /></div>
            <div><Label className="mb-1 block text-sm">Something I want people to know</Label><Textarea value={editing.fun_fact} maxLength={300} onChange={(event) => setEditing({ ...editing, fun_fact: event.target.value })} placeholder="Ask me about returns and warranties." /></div>
            <div><Label className="mb-1 block text-sm">Outside work</Label><Input value={editing.hobby} maxLength={120} onChange={(event) => setEditing({ ...editing, hobby: event.target.value })} placeholder="Running, jazz, bad puns" /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button type="submit">Save</Button></div>
          </form>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
